import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
  Chip,
  IconButton,
  Rating
} from '@mui/material';
import {
  Recommend as RecommendIcon,
  ArrowForward as ArrowIcon,
  LocationOn as LocationIcon,
  Restaurant as RestaurantIcon,
  LocalActivity as ActivityIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

const safeAge = (ts) => {
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return formatDistanceToNow(d, { addSuffix: true });
  } catch { return ''; }
};

const RecommendationsWidget = ({ recommendations, loading }) => {
  const formatLocation = (rec) => {
    if (!rec) return null;
    if (typeof rec.location === 'string') return rec.location;
    if (rec.address) return rec.address;
    if (rec.location && typeof rec.location === 'object') {
      const { lat, lng } = rec.location;
      if (lat == null && lng == null) return null;
      return `Lat ${lat ?? '—'}, Lng ${lng ?? '—'}`;
    }
    return null;
  };
  const getCategoryIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'restaurant':
        return <RestaurantIcon sx={{ fontSize: 16 }} />;
      case 'activity':
        return <ActivityIcon sx={{ fontSize: 16 }} />;
      default:
        return <LocationIcon sx={{ fontSize: 16 }} />;
    }
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          avatar={<RecommendIcon color="primary" />}
          title="Recommendations"
        />
        <CardContent sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!recommendations || recommendations.length === 0) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          avatar={<RecommendIcon color="primary" />}
          title="Recommendations"
          action={
            <IconButton component={Link} to="/recommendations">
              <ArrowIcon />
            </IconButton>
          }
        />
        <CardContent sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No recommendations yet. Share your favorite places!
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        avatar={<RecommendIcon color="primary" />}
        title="Family Recommendations"
        subheader="Places your family loves"
        action={
          <IconButton component={Link} to="/recommendations" title="View all">
            <ArrowIcon />
          </IconButton>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <List dense>
          {recommendations.map((rec) => (
            <ListItem
              key={rec.id}
              sx={{
                borderLeft: 3,
                borderColor: 'success.main',
                borderRadius: 1,
                mb: 1,
                bgcolor: 'background.default',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {getCategoryIcon(rec.category)}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {rec.title || rec.name}
                    </Typography>
                    {rec.category && (
                      <Chip label={rec.category} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                    )}
                  </Box>
                }
                secondary={
                  <Box component="span" sx={{ display: 'block' }}>
                    {rec.rating && (
                      <Rating value={rec.rating} size="small" readOnly sx={{ mb: 0.5 }} />
                    )}
                    {rec.description && (
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {rec.description.length > 80
                          ? `${rec.description.substring(0, 80)}...`
                          : rec.description}
                      </Typography>
                    )}
                    {formatLocation(rec) && (
                      <Typography component="span" variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                        📍 {formatLocation(rec)}
                      </Typography>
                    )}
                    {rec.createdBy && (
                      <Typography component="span" variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                        Recommended by {rec.createdBy}
                        {rec.createdAt && ` • ${safeAge(rec.createdAt)}`}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default RecommendationsWidget;
